// Shared, dependency-free number formatting for numeric text elements (Price
// above all). Both the editor-tree and FanWeb-tree components import it and the
// studio node unit suite requires it directly, so it is CJS, no deps.
//
// Formats are named after the shape they produce rather than a locale, because
// the author picks the look they want on the card, not the viewer's locale.

const FORMAT_AUTO = "auto";

// group: thousands separator ("" = none). decimal: decimal separator.
// decimals: fixed decimal places.
const SPECS = {
  [FORMAT_AUTO]: { raw: true },
  "0": { group: "", decimal: "", decimals: 0 },
  "0.00": { group: "", decimal: ".", decimals: 2 },
  "0,00": { group: "", decimal: ",", decimals: 2 },
  "0,000": { group: ",", decimal: "", decimals: 0 },
  "0.000": { group: ".", decimal: "", decimals: 0 },
  "0 000": { group: " ", decimal: "", decimals: 0 },
  "0,000.00": { group: ",", decimal: ".", decimals: 2 },
  "0.000,00": { group: ".", decimal: ",", decimals: 2 },
};

const NUMBER_FORMAT_KEYS = Object.keys(SPECS);

// Picker rows. `name` carries a worked example so the author can see the shape
// without applying it first.
const NUMBER_FORMAT_ITEMS = [
  { id: FORMAT_AUTO, name: "As entered — 1234.5" },
  { id: "0", name: "Whole number — 1235" },
  { id: "0.00", name: "0.00 — 1234.50" },
  { id: "0,00", name: "0,00 — 1234,50" },
  { id: "0,000", name: "0,000 — 1,235" },
  { id: "0,000.00", name: "0,000.00 — 1,234.50" },
  { id: "0.000,00", name: "0.000,00 — 1.234,50" },
];

// Whole-number elements (like/comment/share counts, vote scores, episode and
// season numbers) are integers, so they are offered grouping only — a decimal
// place on a like count is meaningless.
const INTEGER_FORMAT_ITEMS = [
  { id: FORMAT_AUTO, name: "As entered — 1234" },
  { id: "0,000", name: "0,000 — 1,234" },
  { id: "0.000", name: "0.000 — 1.234" },
  { id: "0 000", name: "0 000 — 1 234" },
];

function addGrouping(whole, separator) {
  if (!separator) {
    return whole;
  }
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

// Returns "" for anything that is not a real number, so an unhydrated entity
// renders empty rather than "NaN". A genuine 0 always formats to "0" — it is a
// price, not an absence, and `value || ""` further up the tree used to eat it.
function formatNumber(value, format = FORMAT_AUTO) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "";
  }

  const spec = SPECS[format] || SPECS[FORMAT_AUTO];
  if (spec.raw) {
    return String(number);
  }

  const fixed = Math.abs(number).toFixed(spec.decimals);
  const [whole, fraction] = fixed.split(".");
  const sign = number < 0 ? "-" : "";
  const grouped = addGrouping(whole, spec.group);

  return fraction ? `${sign}${grouped}${spec.decimal}${fraction}` : `${sign}${grouped}`;
}

function formatFieldText(value, { format = FORMAT_AUTO, prefix = "", suffix = "" } = {}) {
  if (value === null || value === undefined || value === "") {
    return value;
  }
  const text = format && format !== FORMAT_AUTO ? formatNumber(value, format) : value;
  return `${prefix || ""}${text}${suffix || ""}`;
}

module.exports = {
  FORMAT_AUTO,
  formatFieldText,
  NUMBER_FORMAT_KEYS,
  NUMBER_FORMAT_ITEMS,
  INTEGER_FORMAT_ITEMS,
  formatNumber,
};
